import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  IPlatformToolComponent,
  IPlatformToolHandler
} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommandResponse} from '@shared/commands/command-base';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {select, Store} from '@ngrx/store';
import {AdminPanelState} from '@features/admin-panel/store/reducers/admin-panel.reducer';
import {getSelectedUser, getSelectedUserNote} from '@features/admin-panel/store/selectors/admin-panel.selector';
import {
  GetUserDetailAction,
  GetUserNoteAction,
  UpdateUserAction,
  UpdateUserNoteAction
} from '@features/admin-panel/store/actions/admin-panel.action';
import {UpdateUserCommand, UpdateUserNoteCommand} from '@features/admin-panel/commands/admin-panel.command';
import {UserModel} from '@features/admin-panel/models/user.model';
import {AdminPanelUserDetailHandler} from '@features/admin-panel/platform-tool-handlers/admin-panel-user-detail-handler';

@Component({
  standalone: false,
  selector: 'app-admin-panel-user-detail-form',
  templateUrl: './admin-panel-user-detail-form.component.html',
  styleUrls: ['./admin-panel-user-detail-form.component.scss']
})
export class AdminPanelUserDetailFormComponent implements OnInit, OnDestroy, IPlatformToolComponent {

  handler: IPlatformToolHandler;
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  detailForm: FormGroup;
  user: any = null;
  isEditing: {firstName: boolean; lastName: boolean; userNote: boolean} = {
    firstName: false,
    lastName: false,
    userNote: false
  };

  private unsubscribe$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<AdminPanelState>
  ) {}

  ngOnInit(): void {
    this.initForm();

    this.store.pipe(select(getSelectedUser), takeUntil(this.unsubscribe$)).subscribe(u => {
      this.user = u;
      if (this.user) {
        this.detailForm.controls['firstName'].enable();
        this.detailForm.controls['lastName'].enable();
        this.detailForm.controls['isActive'].enable();
        this.detailForm.controls['firstName'].setValue(this.user.firstName);
        this.detailForm.controls['lastName'].setValue(this.user.lastName);
        this.detailForm.controls['companyName'].setValue(this.user.companyName);
        this.detailForm.controls['designation'].setValue(this.user.designation);
        this.detailForm.controls['email'].setValue(this.user.email);
        this.detailForm.controls['isActive'].setValue(this.user.isActive);
        // Share user with handler for dialog-backed actions
        if (this.handler && (this.handler as AdminPanelUserDetailHandler).currentUser !== undefined) {
          (this.handler as AdminPanelUserDetailHandler).currentUser = this.user;
        }
      }
    });

    this.store.pipe(select(getSelectedUserNote), takeUntil(this.unsubscribe$)).subscribe(note => {
      if (note) {
        this.detailForm.controls['userNote'].enable();
        this.detailForm.controls['userNote'].setValue(note.notes);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onLoad(): void {
    const userName = this.selectedTab?.tabData?.['UserName'];
    if (userName) {
      this.store.dispatch(new GetUserDetailAction(userName));
      this.store.dispatch(new GetUserNoteAction(userName));
    }
  }

  onAfterExecuteNextCommand(nextCommandResponse: PlatformCommandResponse): void {}
  onAfterExecutePreviousCommand(previousCommandResponse: PlatformCommandResponse): void {}
  onBeforeExecuteNextCommand(): boolean { return false; }
  onBeforeExecutePreviousCommand(): boolean { return false; }

  private initForm(): void {
    this.detailForm = this.formBuilder.group({
      firstName: [''],
      lastName: [''],
      companyName: [''],
      designation: [''],
      email: [''],
      userNote: [''],
      isActive: ['']
    });
    this.detailForm.disable();
  }

  editField(field: string): void {
    this.resetEditing();
    this.isEditing[field] = true;
  }

  resetEditing(): void {
    this.isEditing = {firstName: false, lastName: false, userNote: false};
  }

  updateFirstName(event: any): void {
    this.detailForm.controls['firstName'].setValue(event.text);
    this.resetEditing();
    this.updateUserData();
  }

  updateLastName(event: any): void {
    this.detailForm.controls['lastName'].setValue(event.text);
    this.resetEditing();
    this.updateUserData();
  }

  updateUserNote(event: any): void {
    this.detailForm.controls['userNote'].setValue(event.text);
    this.resetEditing();
    this.updateNoteData();
  }

  updateUserData(): void {
    const firstName = this.detailForm.controls['firstName'].value?.trim();
    const lastName = this.detailForm.controls['lastName'].value?.trim();
    this.detailForm.controls['firstName'].setValue(firstName);
    this.detailForm.controls['lastName'].setValue(lastName);
    const user = new UserModel({
      firstName,
      lastName,
      companyName: this.user?.companyName,
      designation: this.user?.designation,
      isActive: this.detailForm.controls['isActive'].value,
      username: this.user?.userName,
      email: this.user?.email,
      companyId: this.user?.companyId
    });
    this.store.dispatch(new UpdateUserAction(new UpdateUserCommand(user)));
  }

  updateNoteData(): void {
    const notes = this.detailForm.controls['userNote'].value?.trim();
    this.detailForm.controls['userNote'].setValue(notes);
    const userNoteObj = {
      userName: this.user?.userName,
      notes
    };
    this.store.dispatch(new UpdateUserNoteAction(new UpdateUserNoteCommand(userNoteObj)));
  }
}
